/**
 * statusHighlight.js
 * ─────────────────────────────────────────────────────────────
 * Shared status-based row highlight utility for Nearzon Admin & Seller panels.
 * Provides consistent, soft highlight colours across all tables.
 *
 * Usage (Ant Design Table rowClassName):
 *   rowClassName={(record) => getOrderRowClass(record.status)}
 *
 * Usage (inline style via onRow):
 *   onRow={(r) => ({ style: getOrderRowStyle(r.status) })}
 * ─────────────────────────────────────────────────────────────
 */

// ─── Order Statuses (from API constant.util.js) ──────────────────────────────

/** Only newly placed orders that need immediate seller/admin attention */
export const PENDING_ORDER_STATUSES = new Set([
  'ordered',
  'requested',
  'return_requested'
]);

/** Statuses that are in active processing (confirmed → en route) */
export const ACTIVE_ORDER_STATUSES = new Set([
  'confirmed',
  'assigned',
  'packed',
  'picked_up',
  'shipped',
]);

/** Terminal statuses — no action required */
export const TERMINAL_ORDER_STATUSES = new Set([
  'delivered',
  'cancelled',
  'delivery_missed',
  'no_delivery_available',
  'returned',
  'return_rejected',
]);

// ─── Request/Approval Statuses ────────────────────────────────────────────────

export const PENDING_APPROVAL_STATUSES = new Set([
  'pending',
]);

export const APPROVED_STATUSES = new Set([
  'approved',
]);

export const REJECTED_STATUSES = new Set([
  'rejected',
]);

// ─── Color Tokens ─────────────────────────────────────────────────────────────

/**
 * Soft highlight styles — intentionally light to not fight column content.
 * Border-left acts as an accent stripe for instant visual scanning.
 */
export const ROW_HIGHLIGHT = {
  /** New/unconfirmed order — warm amber-orange */
  pending_order: {
    backgroundColor: '#e8f5e9', // Light green
    borderLeft: '3px solid #2e7d32', // Dark green
  },
  /** Active processing order — soft indigo-blue */
  active_order: {
    backgroundColor: '#f5f8ff',
    borderLeft: '3px solid #6366f1',
  },
  /** Pending approval request (shop/product/seller) — warm amber */
  pending_approval: {
    backgroundColor: '#fffbeb',
    borderLeft: '3px solid #f59e0b',
  },
  /** Approved — soft green */
  approved: {
    backgroundColor: '#f0fdf4',
    borderLeft: '3px solid #22c55e',
  },
  /** Rejected — soft red */
  rejected: {
    backgroundColor: '#fef2f2',
    borderLeft: '3px solid #ef4444',
  },
  /** Default — no highlight */
  default: {},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns the effective status of an order, taking into account returnStatus and productIssueRequest.
 * @param {object|string} orderOrStatus - The order object or status string.
 * @returns {string} The effective status string.
 */
export const getEffectiveStatus = (orderOrStatus) => {
    if (typeof orderOrStatus === 'string') {
        return orderOrStatus.toLowerCase();
    }
    const order = orderOrStatus;
    if (!order) return '';
    const rs = order?.returnStatus;
    if (!rs || rs === 'none') {
        if (order?.status === 'order_updated' || (order?.productIssueRequest?.status === 'ACCEPTED' && ['ordered', 'confirmed', 'packed'].includes(order?.status?.toLowerCase()))) {
            return 'order_updated';
        }
        return order?.status?.toLowerCase();
    }
    return rs.toLowerCase();
};

/**
 * Returns an inline style object for an order row based on its status.
 * @param {string} status - The order status string.
 * @returns {object} React inline style object.
 */
export const getOrderRowStyle = (status) => {
  const s = status?.toLowerCase();
  if (PENDING_ORDER_STATUSES.has(s)) return ROW_HIGHLIGHT.pending_order;
  if (ACTIVE_ORDER_STATUSES.has(s)) return ROW_HIGHLIGHT.active_order;
  return ROW_HIGHLIGHT.default;
};

/**
 * Returns a CSS class name string for an order row based on its status.
 * (Requires corresponding CSS to be defined — fallback to inline style for safety.)
 * @param {string} status - The order status string.
 * @returns {string} CSS class name.
 */
export const getOrderRowClass = (orderOrStatus) => {
  const s = getEffectiveStatus(orderOrStatus);
  if (s === 'order_updated') return 'reordered-row-highlight';
  if (PENDING_ORDER_STATUSES.has(s)) return 'row-highlight-pending-order';
  if (ACTIVE_ORDER_STATUSES.has(s)) return 'row-highlight-active-order';
  return '';
};

/**
 * Returns an inline style object for an approval-request row (Shop, Product, Seller).
 * @param {string} status - The approval status string (pending/approved/rejected).
 * @returns {object} React inline style object.
 */
export const getApprovalRowStyle = (status) => {
  const s = status?.toLowerCase();
  if (PENDING_APPROVAL_STATUSES.has(s)) return ROW_HIGHLIGHT.pending_approval;
  if (APPROVED_STATUSES.has(s)) return ROW_HIGHLIGHT.approved;
  if (REJECTED_STATUSES.has(s)) return ROW_HIGHLIGHT.rejected;
  return ROW_HIGHLIGHT.default;
};

/**
 * Returns a CSS class name string for an approval-request row.
 * @param {string} status - The approval status string.
 * @returns {string} CSS class name.
 */
export const getApprovalRowClass = (status) => {
  const s = status?.toLowerCase();
  if (PENDING_APPROVAL_STATUSES.has(s)) return 'row-highlight-pending-approval';
  if (APPROVED_STATUSES.has(s)) return 'row-highlight-approved';
  if (REJECTED_STATUSES.has(s)) return 'row-highlight-rejected';
  return '';
};
